// Brewing-stand window (UI; MB.4). Three bottle slots + an ingredient slot + a
// blaze-powder fuel slot over a brewing block entity, plus the player inventory.
// Brewing runs in the tick loop (phase 6); the window polls a repaint so the
// brewed potions appear live. Client-only (ENG-1).
import {
  brewingSlots, BREW_BOTTLES, BREW_INGREDIENT, BREW_FUEL, type BrewingData,
} from '../../../sim/game/containers/brewing';
import { maxStackFn, playerRefs, playerRows } from './crafting';
import { openWindow, refRange, returnToInventory, type SlotRef, type WindowConfig } from './container';
import type { Game } from '../../boot';

/** Open a brewing stand's window. */
export function openBrewing(game: Game, data: BrewingData): () => void {
  const slots = brewingSlots(data, game.registries, maxStackFn(game.registries));
  const refs = refRange(slots, 0, 5, undefined, 'brewing');
  const [b0, b1, b2, ingredient, fuel] = refs;
  const pr = playerRefs(game.playerState.inventory);
  const brewSet = new Set<object>([slots]);

  // Ingredient on top, the three bottles in a row below it, fuel to the side.
  const layout: (SlotRef | null)[][] = [
    [null, ingredient!, null, fuel!],
    [b0!, b1!, b2!],
    [null],
    ...playerRows(pr),
  ];

  let stop: (() => void) | null = null;
  const cfg: WindowConfig = {
    title: 'Brewing Stand',
    layout,
    quickTargets: (ref) => {
      if (brewSet.has(ref.slots)) return [...pr.storage, ...pr.hotbar];
      // Player → stand: blaze powder to the fuel slot, a potion/bottle to a free
      // bottle slot, everything else to the ingredient slot.
      const stack = ref.slots.get(ref.index);
      if (slots.accepts(BREW_FUEL, stack)) return [fuel!];
      const name = game.registries.items.nameOf[stack.item] ?? '';
      if (name === 'potion' || name === 'glass_bottle') return [b0!, b1!, b2!];
      return [ingredient!, b0!, b1!, b2!];
    },
    onOpen: (repaint) => {
      const timer = window.setInterval(repaint, 120); // keep the brew progress live
      stop = () => window.clearInterval(timer);
    },
    onClose: (cursor) => { stop?.(); returnToInventory(game, [cursor]); },
  };
  void BREW_BOTTLES; void BREW_INGREDIENT;
  return openWindow(game, cfg);
}
